import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import TableContainer from "components/Common/TableContainer";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  getPublicVenues,
  createPublicVenue,
  updatePublicVenue,
  deletePublicVenue,
} from "api";
import { handleError } from "utils/errorHandler";
import ImageCropper from "../../../components/Common/ImageCropper";

const PublicVenueList = () => {
  document.title = "Public Venue List | EventoX";

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState("");
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedCropFile, setSelectedCropFile] = useState(null);

  useEffect(() => {
    try {
      const auth = localStorage.getItem("authUser");
      if (auth) {
        const parsed = JSON.parse(auth);
        const id = parsed?.user?.userId ?? parsed?.userId;
        if (id != null) setCurrentUserId(String(id));
      }
    } catch (_) {}
  }, []);

  const toggle = () => setModal(!modal);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueName: selectedItem?.venueName ?? "",
      location: selectedItem?.location ?? "",
      capacity: selectedItem?.capacity ?? "",
      amenities: Array.isArray(selectedItem?.amenities) ? selectedItem.amenities.join(", ") : (selectedItem?.amenities ?? ""),
      email: selectedItem?.email ?? "",
      phoneNo: selectedItem?.phoneNo ?? "",
      isVerified: selectedItem?.isVerified ?? false,
      createdBy: selectedItem?.createdBy ?? currentUserId ?? "",
      venueBanner: null,
    },
    validationSchema: Yup.object({
      createdBy: Yup.string().required("Created by is required"),
    }),
    onSubmit: async (values) => {
      setSubmitLoading(true);
      try {
        const hasFile = values.venueBanner instanceof File;
        const id = selectedItem?.id ?? selectedItem?.venueId;

        if (hasFile) {
          const amenitiesData = values.amenities 
            ? (typeof values.amenities === 'string' ? values.amenities.split(",").map(t => t.trim()).filter(t => t !== "") : values.amenities)
            : [];

          const formData = new FormData();
          formData.append("createdBy", String(values.createdBy));
          if (values.venueName) formData.append("venueName", values.venueName);
          if (values.location) formData.append("location", values.location);
          if (values.capacity) formData.append("capacity", String(values.capacity));
          if (values.amenities) formData.append("amenities", JSON.stringify(amenitiesData));
          if (values.email) formData.append("email", values.email);
          if (values.phoneNo) formData.append("phoneNo", values.phoneNo);
          formData.append("isVerified", values.isVerified ? "true" : "false");
          formData.append("venueBanner", values.venueBanner);

          if (isEdit && id) {
            await updatePublicVenue(id, formData);
            toast.success("Venue updated");
          } else {
            await createPublicVenue(formData);
            toast.success("Venue created");
          }
        } else {
          const amenitiesData = values.amenities 
            ? (typeof values.amenities === 'string' ? values.amenities.split(",").map(t => t.trim()).filter(t => t !== "") : values.amenities)
            : [];

          const payload = {
            createdBy: Number(values.createdBy),
            venueName: values.venueName || undefined,
            location: values.location || undefined,
            capacity: values.capacity ? Number(values.capacity) : undefined,
            amenities: amenitiesData,
            email: values.email || undefined,
            phoneNo: values.phoneNo || undefined,
            isVerified: Boolean(values.isVerified),
          };
          if (isEdit && id) {
            await updatePublicVenue(id, payload);
            toast.success("Venue updated");
          } else {
            await createPublicVenue(payload);
            toast.success("Venue created");
          }
        }

        const data = await getPublicVenues();
        setVenues(Array.isArray(data) ? data : []);
        validation.resetForm();
        setSelectedItem(null);
        setIsEdit(false);
        toggle();
      } catch (err) {
        handleError(err);
        toast.error("Error saving venue");
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  useEffect(() => {
    let url = null;
    if (validation.values.venueBanner instanceof File) {
      url = URL.createObjectURL(validation.values.venueBanner);
      setBannerPreviewUrl(url);
    } else {
      setBannerPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [validation.values.venueBanner]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPublicVenues();
        setVenues(Array.isArray(data) ? data : []);
      } catch (err) {
        handleError(err);
        toast.error("Failed to load venues");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onClickDelete = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    const id = selectedItem?.id ?? selectedItem?.venueId;
    if (!id) {
      setDeleteModal(false);
      return;
    }
    try {
      await deletePublicVenue(id);
      const data = await getPublicVenues();
      setVenues(Array.isArray(data) ? data : []);
      toast.success("Venue deleted");
    } catch (err) {
      handleError(err);
      toast.error("Error deleting venue");
    }
    setDeleteModal(false);
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEdit(true);
    validation.resetForm();
    toggle();
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsEdit(false);
    validation.setFieldValue("createdBy", currentUserId || "");
    validation.resetForm();
    toggle();
  };

  const columns = useMemo(
    () => [
      {
        header: "Banner",
        accessorKey: "venueBanner",
        cell: (cell) => {
          const row = cell.row.original;
          const img = row.venueBanner ?? row.bannerUrl;
          const name = row.venueName || "";
          return (
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0 me-2">
                {img ? (
                  <img
                    src={img}
                    alt={name}
                    className="rounded"
                    style={{ width: 48, height: 36, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="rounded bg-light d-flex align-items-center justify-content-center text-muted"
                    style={{ width: 48, height: 36, fontSize: "10px" }}
                  >
                    No img
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      { header: "Venue Name", accessorKey: "venueName", cell: (c) => c.getValue() || "—" },
      { header: "Location", accessorKey: "location", cell: (c) => c.getValue() || "—" },
      { header: "Capacity", accessorKey: "capacity", cell: (c) => c.getValue() ?? "—" },
      { 
        header: "Amenities", 
        accessorKey: "amenities", 
        cell: (cell) => {
          const value = cell.getValue();
          if (!value) return "—";
          const list = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(",").filter(v => v.trim() !== "") : []);
          if (list.length === 0) return "—";
          return (
            <div className="d-flex flex-wrap gap-1" style={{ maxWidth: "200px" }}>
              {list.slice(0, 2).map((a, i) => (
                <span key={i} className="badge badge-soft-primary text-truncate" style={{ maxWidth: "80px" }}>{a.trim()}</span>
              ))}
              {list.length > 2 && (
                <span className="badge badge-soft-secondary">+{list.length - 2}</span>
              )}
            </div>
          );
        }
      },
      { header: "Email", accessorKey: "email", cell: (c) => c.getValue() ?? "—" },
      { header: "Phone", accessorKey: "phoneNo", cell: (c) => c.getValue() ?? "—" },
      {
        header: "Verified",
        accessorKey: "isVerified",
        cell: (c) =>
          c.getValue() ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-secondary">No</span>
          ),
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => handleEdit(item)}>Edit</DropdownItem>
                <DropdownItem onClick={() => onClickDelete(item)}>Delete</DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Public Venue List" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={venues}
                      isGlobalFilter
                      isPagination
                      SearchPlaceholder="Search venues..."
                      isAddButton
                      handleUserClick={handleAddNew}
                      buttonClass="btn btn-success btn-rounded mb-2"
                      buttonName="Add Venue"
                      tableClass="align-middle table-hover w-100"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate pagination-rounded"
                      pagination="pagination"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} size="lg" scrollable>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Public Venue" : "Add Public Venue"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
              >
                {/* <div className="mb-3">
                  <Label>Created By (User ID) *</Label>
                  <Input
                    name="createdBy"
                    type="number"
                    min={1}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.createdBy}
                    invalid={validation.touched.createdBy && validation.errors.createdBy}
                  />
                  <FormFeedback>{validation.errors.createdBy}</FormFeedback>
                </div> */}

                <div className="mb-3">
                  <Label>Venue Name</Label>
                  <Input
                    name="venueName"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.venueName}
                    placeholder="e.g. Grand Hall"
                  />
                </div>

                <div className="row">
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Location</Label>
                      <Input
                        name="location"
                        type="text"
                        onChange={validation.handleChange}
                        value={validation.values.location}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Capacity</Label>
                      <Input
                        name="capacity"
                        type="number"
                        min={0}
                        onChange={validation.handleChange}
                        value={validation.values.capacity}
                      />
                    </div>
                  </Col>
                </div>

                <div className="mb-3">
                  <Label>Amenities (Comma separated)</Label>
                  <Input
                    name="amenities"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.amenities}
                    placeholder="e.g. Parking, WiFi, Stage"
                  />
                </div>

                <div className="row">
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Email</Label>
                      <Input
                        name="email"
                        type="email"
                        onChange={validation.handleChange}
                        value={validation.values.email}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Phone</Label>
                      <Input
                        name="phoneNo"
                        type="text"
                        onChange={validation.handleChange}
                        value={validation.values.phoneNo}
                      />
                    </div>
                  </Col>
                </div>

                <div className="mb-3">
                  <Label>Banner Image</Label>
                  <Input
                    name="venueBanner"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedCropFile(file);
                        setIsCropperOpen(true);
                      }
                      e.target.value = '';
                    }}
                  />
                  {bannerPreviewUrl && (
                    <img
                      src={bannerPreviewUrl}
                      alt="Banner preview"
                      className="mt-2 rounded"
                      style={{ maxHeight: 100, maxWidth: "100%" }}
                    />
                  )}
                  {isEdit && selectedItem?.venueBanner && !validation.values.venueBanner && (
                    <p className="mt-2 small text-muted mb-0">
                      Current: <a href={selectedItem.venueBanner} target="_blank" rel="noopener noreferrer">View</a>
                    </p>
                  )}
                </div>

                <div className="mb-3">
                  <Label check>
                    <Input
                      name="isVerified"
                      type="checkbox"
                      checked={validation.values.isVerified}
                      onChange={validation.handleChange}
                    />
                    Verified
                  </Label>
                </div>

                <div className="text-end">
                  <Button color="secondary" className="me-2" onClick={toggle}>
                    Cancel
                  </Button>
                  <Button color="success" type="submit" disabled={submitLoading}>
                    {submitLoading ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>

      <Modal isOpen={isCropperOpen} toggle={() => setIsCropperOpen(false)} size="lg" style={{ zIndex: 1060 }}>
        <ModalHeader toggle={() => setIsCropperOpen(false)}>Crop Image</ModalHeader>
        <ModalBody>
          {selectedCropFile && (
            <ImageCropper
              imageFile={selectedCropFile}
              aspect={16/9}
              onCropComplete={(croppedFile) => {
                validation.setFieldValue("venueBanner", croppedFile);
                setIsCropperOpen(false);
              }}
            />
          )}
        </ModalBody>
      </Modal>

    </>
  );
};

export default withRouter(PublicVenueList);
